import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Modal,
  Alert,
  Spinner,
  Badge,
  Tabs,
  Tab,
  Table,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaBuilding, FaCalendarAlt, FaUser } from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { toast } from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { api } from "../../../contexts/AuthContext";

const ACCENT = "#60A5FA";

const PRIORITY_VALUES = { low: 25, medium: 50, high: 80 };

const emptyForm = (kind = "request") => ({
  kind,
  title: "",
  description: "",
  due_date: null,
  status: "todo",
  priority_level: "medium",
  company: "",
  location: "",
  with_person: "",
});

const kindLabel = (kind) => {
  if (kind === "appointment") return "Appointment";
  if (kind === "personal") return "Personal";
  return "Request";
};

const kindBadge = (kind) => {
  if (kind === "appointment") return "info";
  if (kind === "personal") return "warning";
  return "secondary";
};

const createSuccessMessage = (kind) => {
  if (kind === "appointment") return "Appointment created";
  if (kind === "personal") return "Personal todo created";
  return "Request created";
};

const createModalTitle = (kind) => {
  if (kind === "appointment") return "New appointment";
  if (kind === "personal") return "New personal todo";
  return "New customer request";
};

const createTitlePlaceholder = (kind) => {
  if (kind === "appointment") return "e.g. Dentist checkup";
  if (kind === "personal") return "e.g. Visit the doctor / Buy groceries";
  return "e.g. Fix login bug";
};

const formatDate = (dateString, withTime = false) => {
  if (!dateString) return "—";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    ...(withTime
      ? { hour: "2-digit", minute: "2-digit" }
      : {}),
  });
};

const levelFromPriority = (priority) => {
  const value = parseInt(priority, 10) || 50;
  if (value >= 80) return "high";
  if (value >= 50) return "medium";
  return "low";
};

const priorityColor = (level) => {
  if (level === "high") return "#dc3545";
  if (level === "medium") return "#fd7e14";
  return "#28a745";
};

const statusLabel = (status) => {
  if (status === "in_progress") return "In progress";
  if (status === "done") return "Done";
  return "To do";
};

const statusVariant = (status) => {
  if (status === "done") return "success";
  if (status === "in_progress") return "primary";
  return "secondary";
};

const isTaskOverdue = (task) => {
  if (task.is_overdue) return true;
  if (!task.due_date || task.status === "done") return false;
  return new Date(task.due_date) < new Date();
};

const isDueToday = (task) => {
  if (!task.due_date) return false;
  const due = new Date(task.due_date);
  const now = new Date();
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  );
};

const AllTasks = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(emptyForm("request"));

  const [searchQuery, setSearchQuery] = useState("");
  const [kindFilter, setKindFilter] = useState("all"); // all | request | appointment
  const [quickFilters, setQuickFilters] = useState({
    overdue: false,
    dueToday: false,
    highPriority: false,
  });
  const [companyFilter, setCompanyFilter] = useState("");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [activeTab, setActiveTab] = useState("table");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [tasksRes, companiesRes] = await Promise.all([
        api.get("/api/tasks/"),
        api.get("/api/companies/"),
      ]);
      setTasks(tasksRes.data);
      setCompanies(companiesRes.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      if (err.response?.status === 401) {
        setError("Your session has expired. Please log in again.");
        navigate("/login");
      } else {
        setError("Failed to fetch tasks. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const openCreate = (kind) => {
    setForm(emptyForm(kind));
    setShowCreateModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (form.kind === "appointment" && !form.due_date) {
      toast.error("Appointments need a date and time");
      return;
    }

    try {
      const payload = {
        kind: form.kind,
        title: form.title.trim(),
        description: form.description?.trim() || "",
        due_date: form.due_date ? new Date(form.due_date).toISOString() : null,
        status: form.status || "todo",
        priority: PRIORITY_VALUES[form.priority_level] || 50,
        company: form.company ? parseInt(form.company, 10) : null,
        location: form.location?.trim() || "",
        with_person: form.with_person?.trim() || "",
      };

      const response = await api.post("/api/tasks/", payload);
      setTasks((prev) => [response.data, ...prev]);
      setShowCreateModal(false);
      setForm(emptyForm("request"));
      toast.success(createSuccessMessage(form.kind));
    } catch (err) {
      console.error("Error creating task:", err);
      const msg =
        err.response?.data?.due_date?.[0] ||
        err.response?.data?.detail ||
        (typeof err.response?.data === "object"
          ? JSON.stringify(err.response.data)
          : "Failed to create");
      toast.error(msg);
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm("Delete this item?")) return;
    try {
      await api.delete(`/api/tasks/${taskId}/`);
      setTasks((prev) => prev.filter((t) => t.id !== taskId));
      toast.success("Deleted");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete");
    }
  };

  const handleQuickStatus = async (task, status) => {
    try {
      const response = await api.patch(`/api/tasks/${task.id}/`, { status });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? response.data : t)));
      toast.success("Status updated");
    } catch (err) {
      console.error(err);
      toast.error("Failed to update status");
    }
  };

  const getFilteredTasks = () => {
    let filtered = [...tasks];

    if (kindFilter !== "all") {
      filtered = filtered.filter((t) => t.kind === kindFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (t) =>
          t.title.toLowerCase().includes(q) ||
          t.description?.toLowerCase().includes(q) ||
          t.with_person?.toLowerCase().includes(q) ||
          t.location?.toLowerCase().includes(q) ||
          t.company?.name?.toLowerCase().includes(q)
      );
    }

    if (quickFilters.highPriority) {
      filtered = filtered.filter((t) => levelFromPriority(t.priority) === "high");
    }
    if (quickFilters.dueToday) {
      filtered = filtered.filter(isDueToday);
    }
    if (quickFilters.overdue) {
      filtered = filtered.filter(isTaskOverdue);
    }
    if (companyFilter) {
      const id = parseInt(companyFilter, 10);
      filtered = filtered.filter((t) => t.company?.id === id);
    }

    return filtered.sort((a, b) => {
      const aOver = isTaskOverdue(a) ? 0 : 1;
      const bOver = isTaskOverdue(b) ? 0 : 1;
      if (aOver !== bOver) return aOver - bOver;
      if (a.status !== b.status) {
        const order = { todo: 0, in_progress: 1, done: 2 };
        return (order[a.status] ?? 9) - (order[b.status] ?? 9);
      }
      return (b.priority || 0) - (a.priority || 0);
    });
  };

  const filtered = getFilteredTasks();
  const counts = {
    request: tasks.filter((t) => t.kind === "request").length,
    appointment: tasks.filter((t) => t.kind === "appointment").length,
    personal: tasks.filter((t) => t.kind === "personal").length,
    overdue: tasks.filter(isTaskOverdue).length,
    todo: tasks.filter((t) => t.status === "todo").length,
    in_progress: tasks.filter((t) => t.status === "in_progress").length,
    done: tasks.filter((t) => t.status === "done").length,
  };

  const chipStyle = (active) => ({
    borderColor: ACCENT,
    color: active ? "#0b1220" : ACCENT,
    backgroundColor: active ? ACCENT : "transparent",
  });

  const withLabel = (task) => {
    if (task.kind === "appointment") {
      return task.with_person || task.company?.name || "—";
    }
    if (task.kind === "personal") {
      return task.with_person || "Me";
    }
    return task.company?.name || "—";
  };

  const renderTable = () => (
    <div
      style={{
        backgroundColor: "#1e1e1e",
        borderRadius: 8,
        padding: 16,
        border: `1px solid ${ACCENT}33`,
      }}
    >
      <Table hover variant="dark" className="align-middle mb-0">
        <thead>
          <tr>
            <th>Title</th>
            <th>Type</th>
            <th>When</th>
            <th>With / Company</th>
            <th>Priority</th>
            <th>Status</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 && (
            <tr>
              <td colSpan={7} className="text-center text-muted py-4">
                No items match your filters.
              </td>
            </tr>
          )}
          {filtered.map((task) => {
            const level = task.priority_level || levelFromPriority(task.priority);
            const overdue = isTaskOverdue(task);
            return (
              <tr
                key={task.id}
                style={{ cursor: "pointer" }}
                onClick={() => navigate(`/task/${task.id}`)}
              >
                <td>
                  <div
                    style={{
                      textDecoration: task.status === "done" ? "line-through" : "none",
                      color: task.status === "done" ? "#9ca3af" : "#fff",
                    }}
                  >
                    {task.title}
                  </div>
                  {task.kind === "appointment" && task.location ? (
                    <small style={{ color: "#9ca3af", display: "block" }}>{task.location}</small>
                  ) : null}
                </td>
                <td>
                  <Badge bg={kindBadge(task.kind)}>
                    {kindLabel(task.kind)}
                  </Badge>
                </td>
                <td>
                  {formatDate(task.due_date, task.kind === "appointment")}
                  {overdue && (
                    <Badge bg="danger" className="ms-2">
                      Overdue
                    </Badge>
                  )}
                </td>
                <td>
                  <span className="d-inline-flex align-items-center gap-1">
                    {task.kind === "appointment" ? (
                      <FaUser style={{ color: ACCENT }} />
                    ) : task.kind === "personal" ? (
                      <FaUser style={{ color: "#fbbf24" }} />
                    ) : (
                      <FaBuilding style={{ color: ACCENT }} />
                    )}
                    {withLabel(task)}
                  </span>
                </td>
                <td>
                  <span
                    className="badge"
                    style={{
                      backgroundColor: priorityColor(level),
                      color: "#fff",
                      textTransform: "capitalize",
                    }}
                  >
                    {level}
                  </span>
                </td>
                <td>
                  <Badge bg={statusVariant(task.status)}>{statusLabel(task.status)}</Badge>
                </td>
                <td className="text-end" onClick={(e) => e.stopPropagation()}>
                  <div className="d-flex justify-content-end gap-1">
                    {task.status !== "done" && (
                      <Button
                        size="sm"
                        variant="outline-success"
                        onClick={() => handleQuickStatus(task, "done")}
                      >
                        Done
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline-primary"
                      style={{ borderColor: ACCENT, color: ACCENT }}
                      onClick={() => navigate(`/task/${task.id}`)}
                    >
                      <FaEdit />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline-danger"
                      onClick={() => handleDelete(task.id)}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </div>
  );

  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    const cells = [];

    for (let i = 0; i < firstDay; i += 1) {
      cells.push(
        <div
          key={`empty-${i}`}
          style={{
            minHeight: 96,
            backgroundColor: "#161616",
            borderRadius: 4,
            border: "1px solid #333",
          }}
        />
      );
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
      const date = new Date(year, month, day);
      const dayTasks = filtered.filter((task) => {
        if (!task.due_date) return false;
        const d = new Date(task.due_date);
        return d.toDateString() === date.toDateString();
      });
      const isToday = date.toDateString() === new Date().toDateString();

      cells.push(
        <div
          key={day}
          style={{
            minHeight: 96,
            backgroundColor: isToday ? "#222a36" : "#1e1e1e",
            borderRadius: 4,
            border: `1px solid ${isToday ? ACCENT : "#333"}`,
            padding: 8,
          }}
        >
          <div
            style={{
              fontWeight: 600,
              marginBottom: 6,
              color: isToday ? ACCENT : "#fff",
              fontSize: 13,
            }}
          >
            {String(day).padStart(2, "0")}
          </div>
          {dayTasks.slice(0, 4).map((task) => (
            <div
              key={task.id}
              onClick={() => navigate(`/task/${task.id}`)}
              style={{
                fontSize: 11,
                padding: "3px 5px",
                marginBottom: 4,
                borderRadius: 3,
                cursor: "pointer",
                backgroundColor: "#2d2d2d",
                borderLeft: `3px solid ${
                  task.kind === "appointment" ? ACCENT : priorityColor(levelFromPriority(task.priority))
                }`,
                color: task.status === "done" ? "#9ca3af" : "#fff",
                textDecoration: task.status === "done" ? "line-through" : "none",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={task.title}
            >
              {task.kind === "appointment" && task.due_date
                ? `${new Date(task.due_date).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })} · `
                : ""}
              {task.title}
            </div>
          ))}
          {dayTasks.length > 4 && (
            <small className="text-muted">+{dayTasks.length - 4} more</small>
          )}
        </div>
      );
    }

    return (
      <div
        style={{
          backgroundColor: "#1e1e1e",
          borderRadius: 8,
          padding: 16,
          border: `1px solid ${ACCENT}33`,
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0" style={{ color: "#fff" }}>
            {currentDate.toLocaleString("en-GB", { month: "long", year: "numeric" })}
          </h5>
          <div>
            <Button
              size="sm"
              variant="outline-primary"
              className="me-2"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onClick={() => setCurrentDate(new Date(year, month - 1, 1))}
            >
              Prev
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              className="me-2"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onClick={() => setCurrentDate(new Date())}
            >
              Today
            </Button>
            <Button
              size="sm"
              variant="outline-primary"
              style={{ borderColor: ACCENT, color: ACCENT }}
              onClick={() => setCurrentDate(new Date(year, month + 1, 1))}
            >
              Next
            </Button>
          </div>
        </div>
        <div className="d-flex mb-2">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div
              key={d}
              style={{ flex: 1, textAlign: "center", fontWeight: 600, color: "#9ca3af", fontSize: 12 }}
            >
              {d}
            </div>
          ))}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(7, 1fr)",
            gap: 8,
          }}
        >
          {cells}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
        <Spinner animation="border" role="status" />
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ color: "#fff" }}>
      <Row className="mb-3 align-items-center">
        <Col>
          <h2 style={{ color: ACCENT, marginBottom: 4 }}>Tasks</h2>
          <div className="d-flex flex-wrap gap-3" style={{ fontSize: 13, color: "#9ca3af" }}>
            <span>{counts.request} requests</span>
            <span>{counts.personal} personal</span>
            <span>{counts.appointment} appointments</span>
            <span style={{ color: counts.overdue ? "#f87171" : undefined }}>
              {counts.overdue} overdue
            </span>
          </div>
        </Col>
        <Col xs="auto">
          <Dropdown as={ButtonGroup}>
            <Button variant="primary" onClick={() => openCreate("personal")}>
              <FaPlus className="me-2" />
              New todo
            </Button>
            <Dropdown.Toggle split variant="primary" id="new-task-split" />
            <Dropdown.Menu variant="dark">
              <Dropdown.Item onClick={() => openCreate("personal")}>Personal todo</Dropdown.Item>
              <Dropdown.Item onClick={() => openCreate("request")}>Customer request</Dropdown.Item>
              <Dropdown.Item onClick={() => openCreate("appointment")}>
                <FaCalendarAlt className="me-2" />
                Appointment
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      <Row className="mb-3 g-2 align-items-center">
        <Col md={4}>
          <Form.Control
            type="search"
            placeholder="Search title, company, person, location…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="bg-dark text-light border-secondary"
          />
        </Col>
        <Col md={3}>
          <Form.Select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value)}
            className="bg-dark text-light border-secondary"
          >
            <option value="">All companies</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Form.Select>
        </Col>
        <Col md={5} className="d-flex flex-wrap gap-2">
          {[
            { key: "all", label: "All" },
            { key: "personal", label: "Personal" },
            { key: "request", label: "Requests" },
            { key: "appointment", label: "Appointments" },
          ].map((chip) => (
            <Button
              key={chip.key}
              size="sm"
              variant="outline-primary"
              style={chipStyle(kindFilter === chip.key)}
              onClick={() => setKindFilter(chip.key)}
            >
              {chip.label}
            </Button>
          ))}
          <Button
            size="sm"
            variant="outline-primary"
            style={chipStyle(quickFilters.overdue)}
            onClick={() => setQuickFilters((p) => ({ ...p, overdue: !p.overdue }))}
          >
            Overdue
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            style={chipStyle(quickFilters.dueToday)}
            onClick={() => setQuickFilters((p) => ({ ...p, dueToday: !p.dueToday }))}
          >
            Due today
          </Button>
          <Button
            size="sm"
            variant="outline-primary"
            style={chipStyle(quickFilters.highPriority)}
            onClick={() => setQuickFilters((p) => ({ ...p, highPriority: !p.highPriority }))}
          >
            High
          </Button>
        </Col>
      </Row>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "table")}
        className="mb-3 ath-dark-tabs"
      >
        <Tab eventKey="table" title={`Table (${filtered.length})`}>
          {renderTable()}
        </Tab>
        <Tab eventKey="calendar" title="Calendar">
          {renderCalendar()}
        </Tab>
      </Tabs>

      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        contentClassName="bg-dark text-light"
      >
        <Modal.Header closeButton closeVariant="white" style={{ borderBottom: "1px solid #333" }}>
          <Modal.Title>{createModalTitle(form.kind)}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreate}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                required
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-dark text-light border-secondary"
                placeholder={createTitlePlaceholder(form.kind)}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-dark text-light border-secondary"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label style={{ display: "block" }}>
                {form.kind === "appointment" ? "Date & time" : "Due date"}
                {form.kind === "appointment" ? " *" : ""}
              </Form.Label>
              <DatePicker
                selected={form.due_date}
                onChange={(date) => setForm({ ...form, due_date: date })}
                showTimeSelect={form.kind === "appointment"}
                timeIntervals={15}
                dateFormat={form.kind === "appointment" ? "dd/MM/yyyy HH:mm" : "dd/MM/yyyy"}
                className="form-control bg-dark text-light border-secondary"
                placeholderText={
                  form.kind === "appointment" ? "Select date and time" : "Optional due date"
                }
                isClearable={form.kind !== "appointment"}
              />
            </Form.Group>

            {form.kind === "appointment" && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>With (person)</Form.Label>
                  <Form.Control
                    value={form.with_person}
                    onChange={(e) => setForm({ ...form, with_person: e.target.value })}
                    className="bg-dark text-light border-secondary"
                    placeholder="e.g. Dr. Papadopoulos"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Location</Form.Label>
                  <Form.Control
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="bg-dark text-light border-secondary"
                    placeholder="Clinic, address, or link"
                  />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Company (optional)</Form.Label>
                  <Form.Select
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="bg-dark text-light border-secondary"
                  >
                    <option value="">None</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </>
            )}

            {form.kind === "request" && (
              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Select
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="bg-dark text-light border-secondary"
                >
                  <option value="">Select company</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}

            {form.kind === "personal" && (
              <Form.Group className="mb-3">
                <Form.Label>Note / who (optional)</Form.Label>
                <Form.Control
                  value={form.with_person}
                  onChange={(e) => setForm({ ...form, with_person: e.target.value })}
                  className="bg-dark text-light border-secondary"
                  placeholder="e.g. Call clinic first"
                />
              </Form.Group>
            )}

            <Form.Group className="mb-1">
              <Form.Label>Priority</Form.Label>
              <Form.Select
                value={form.priority_level}
                onChange={(e) => setForm({ ...form, priority_level: e.target.value })}
                className="bg-dark text-light border-secondary"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid #333" }}>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit">
              Create
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default AllTasks;
